// ОТК.1: анимация открытия ШК.
//
// Порт из docs/design/design_handoff_crate_opening/reference/crate-scene.jsx
// с адаптацией под наш стек (React+TS, Vite, без сторонних либ).
//
// Сцена 720×1280. Letterbox-скейл к viewport. 8 фаз (см. README.md §5):
//   1. TopBar fade-in (0.15-0.7)
//   2. Crate entrance (0-0.9)
//   3. Auth scan (1.0-1.7)
//   4. Cracks + tremor (1.8-2.2)
//   5. Burst: flash + 3 shockwaves + 18 particles + лид-полёт (2.15-2.6)
//   6. Card barrage (2.45-5.7) — карты летят по arc-Bezier, поп-ин, спин
//   7. Settled (5.7-6.5)
//   8. Summary chip (6.5-8.0) + кнопка Забрать
//
// Скип после 3.0с — тап в любое место → onClaim.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Stage, Easing, T, useTime } from './animation/animations';
import type { OpenedCard, RarityApi } from '../api/client';
import { getCardData, type CardData } from '../lib/cardData';
import { GameCard } from './GameCard';
import { play, preloadSfx } from '../audio/sfx';

// ── Cyber-палитра (совпадает с tokens.css) ─────────────────
const COL = {
  bg: '#07070B',
  cy: '#3DF0FF',
  mg: '#FF3D8F',
  amber: '#FFB13D',
  lime: '#9CFF3D',
  white: '#F4F6FB',
  tMd: '#A0A4B4',
  tLo: '#60657A',
  rCommon: '#A8AEBE',
  rRare:   '#3DF0FF',
  rEpic:   '#C93DFF',
  rLegend: '#FFB13D'
};

// ── Crate variants (handoff §7) ──────────────────────────
type CrateType = 'generic' | 'deus' | 'synt' | 'gsec' | 'liv' | 'legend';
const CRATE_TYPES: Record<CrateType, { lid1: string; lid2: string; accent: string; label: string; sub: string }> = {
  generic: { lid1: '#143D45', lid2: '#06181C', accent: COL.cy,      label: 'ШК', sub: 'ОСНОВНОЙ · ОБЩИЙ' },
  deus:    { lid1: '#3D7DFF', lid2: '#0B1430', accent: '#8FB2FF',   label: 'DS', sub: 'ДЕУС · ФРАКЦ.' },
  synt:    { lid1: '#C93DFF', lid2: '#1F0A26', accent: '#E7FF3D',   label: 'SX', sub: 'СИНТЕТИКС' },
  gsec:    { lid1: '#FF8A1F', lid2: '#1F1508', accent: '#FFC83D',   label: 'GS', sub: 'ГЛОБАЛ СЕК.' },
  liv:     { lid1: '#3DFFA5', lid2: '#0D1F15', accent: '#3DFFA5',   label: 'LV', sub: 'ЛИВ ЛАЙФ' },
  legend:  { lid1: '#5A3A10', lid2: '#1A1208', accent: COL.amber,   label: '★',  sub: 'ВЫСШАЯ ЛИГА' }
};

// Маппинг container.type на CrateType.
export function containerTypeToCrateType(type: string): CrateType {
  if (type === 'main_common' || type === 'basic_common' || type === 'advanced_common') return 'generic';
  const m = type.match(/_(deus|syntetics|global|livlife)$/);
  if (m) {
    return ({ deus: 'deus', syntetics: 'synt', global: 'gsec', livlife: 'liv' } as const)[m[1] as 'deus']
      || 'generic';
  }
  if (type === 'main_faction' || type === 'advanced_faction') return 'generic';
  return 'generic';
}

const RARITY_COLOR: Record<RarityApi, string> = {
  common: COL.rCommon, rare: COL.rRare, epic: COL.rEpic, legendary: COL.rLegend
};
const RARITY_GLOW: Record<RarityApi, string> = {
  common: 'rgba(168,174,190,.1)',
  rare:   'rgba(61,240,255,.25)',
  epic:   'rgba(201,61,255,.4)',
  legendary: 'rgba(255,177,61,.5)'
};

// Маппер фракции карты в 2-буквенный код (для шапки на карте в анимации).
const FACTION_CODE: Record<string, string> = {
  deus: 'DS', syntetics: 'SX', global: 'GS', livlife: 'LV', neutral: 'NU'
};
const FACTION_COLOR: Record<string, string> = {
  DS: '#8FB2FF', SX: '#E7FF3D', GS: '#FFC83D', LV: '#3DFFA5', NU: '#A0A4B4'
};
const FACTION_BG: Record<string, string> = {
  DS: '#0B1430', SX: '#1F0A26', GS: '#1F1508', LV: '#0D1F15', NU: '#15151B'
};

// ─── Background ─────────────────────────────────────────
function Background() {
  const t = useTime();
  const flashAlpha =
    T(t, 2.15, 2.4, 0, 0.9, Easing.easeOutCubic)
    - T(t, 2.4, 3.0, 0, 0.9, Easing.easeInQuad);
  return (
    <div style={{ position: 'absolute', inset: 0, overflow: 'hidden' }}>
      <div style={{
        position: 'absolute', inset: 0,
        background: `
          radial-gradient(ellipse 60% 50% at 50% 55%, rgba(61,240,255,.10), transparent 65%),
          radial-gradient(ellipse 80% 60% at 50% 100%, rgba(201,61,255,.08), transparent 70%),
          linear-gradient(180deg, #050509 0%, #0A0A14 50%, #050509 100%)`
      }} />
      <svg width="720" height="1280" style={{ position: 'absolute', inset: 0, opacity: 0.4 }}>
        <defs>
          <linearGradient id="floor-fade" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={COL.cy} stopOpacity="0" />
            <stop offset="60%" stopColor={COL.cy} stopOpacity=".25" />
            <stop offset="100%" stopColor={COL.cy} stopOpacity="0" />
          </linearGradient>
          <pattern id="scan-pat" x="0" y="0" width="3" height="3" patternUnits="userSpaceOnUse">
            <line x1="0" y1="0" x2="3" y2="0" stroke="#fff" strokeOpacity=".015" />
          </pattern>
        </defs>
        {Array.from({ length: 11 }).map((_, i) => {
          const x = (i / 10) * 1440 - 360;
          return <line key={i} x1={x} y1={1280} x2={360} y2={780} stroke="url(#floor-fade)" strokeWidth="1" />;
        })}
        {[820, 880, 950, 1040, 1150].map((y, i) => (
          <line key={i} x1={-100} y1={y} x2={820} y2={y} stroke={COL.cy} strokeOpacity={0.05 + i * 0.04} strokeWidth="1" />
        ))}
        <rect x="0" y="0" width="720" height="1280" fill="url(#scan-pat)" opacity="0.5" />
      </svg>
      {flashAlpha > 0.01 && (
        <div style={{
          position: 'absolute', inset: 0,
          background: `radial-gradient(circle at 50% 50%, rgba(255,255,255,${flashAlpha}), transparent 55%)`,
          mixBlendMode: 'screen', pointerEvents: 'none'
        }} />
      )}
    </div>
  );
}

// ─── Top bar ────────────────────────────────────────────
function TopBar({ crateType }: { crateType: CrateType }) {
  const t = useTime();
  const cfg = CRATE_TYPES[crateType];
  const op = T(t, 0.15, 0.7, 0, 1, Easing.easeOutCubic);
  const oy = T(t, 0.15, 0.7, -20, 0, Easing.easeOutCubic);
  return (
    <div style={{
      position: 'absolute', top: 64, left: 0, right: 0,
      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6,
      opacity: op, transform: `translateY(${oy}px)`,
      pointerEvents: 'none'
    }}>
      <div style={{
        fontFamily: 'JetBrains Mono, monospace', fontSize: 11, letterSpacing: '.32em',
        color: cfg.accent, textTransform: 'uppercase',
        textShadow: `0 0 12px ${cfg.accent}`
      }}>
        ⟢  ВСКРЫТИЕ ШИФРОКОНТЕЙНЕРА  ⟣
      </div>
      <div style={{
        fontFamily: 'Chakra Petch, sans-serif', fontSize: 32, fontWeight: 700,
        color: COL.white, letterSpacing: '.04em', textTransform: 'uppercase'
      }}>
        {cfg.sub}
      </div>
      <div style={{
        fontFamily: 'JetBrains Mono, monospace', fontSize: 10, letterSpacing: '.24em',
        color: COL.tLo, textTransform: 'uppercase', marginTop: 2
      }}>
        MATCH·REWARD · ID 0x{Math.floor(48241 + t * 100).toString(16).toUpperCase()}
      </div>
    </div>
  );
}

// ─── Particle burst (18 deterministic) ──────────────────
function ParticleBurst() {
  const t = useTime();
  const particles = useMemo(() => (
    Array.from({ length: 18 }, (_, i) => {
      const angle = (i / 18) * Math.PI * 2 + (i % 3) * 0.13;
      const dist = 180 + (i % 5) * 60;
      return {
        angle, dist,
        size: 3 + (i % 4),
        delay: (i % 6) * 0.02,
        color: i % 4 === 0 ? COL.cy : i % 4 === 1 ? COL.amber : i % 4 === 2 ? COL.mg : '#fff',
        round: i % 2 === 1
      };
    })
  ), []);
  if (t < 2.2 || t > 4.5) return null;
  return (
    <div style={{ position: 'absolute', left: 360, top: 640, pointerEvents: 'none' }}>
      {particles.map((p, i) => {
        const local = t - 2.2 - p.delay;
        if (local < 0 || local > 2.2) return null;
        const prog = Math.min(local / 2.2, 1);
        const eased = Easing.easeOutCubic(prog);
        const x = Math.cos(p.angle) * p.dist * eased;
        const y = Math.sin(p.angle) * p.dist * eased + 0.6 * (eased ** 2) * 200;
        const op = (1 - prog) * (prog < 0.1 ? prog * 10 : 1);
        return (
          <div key={i} style={{
            position: 'absolute',
            left: x - p.size / 2, top: y - p.size / 2,
            width: p.size, height: p.size,
            background: p.color, opacity: op,
            boxShadow: `0 0 8px ${p.color}`,
            transform: `rotate(${prog * 720}deg)`,
            borderRadius: p.round ? '50%' : 0
          }} />
        );
      })}
    </div>
  );
}

// ─── Shockwave rings + white flash core ─────────────────
function Shockwave() {
  const t = useTime();
  if (t < 2.15 || t > 3.6) return null;
  const rings = [0, 0.08, 0.18];
  return (
    <svg width={720} height={1280} style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
      {rings.map((d, i) => {
        const local = t - 2.2 - d;
        if (local < 0 || local > 1.2) return null;
        const prog = local / 1.2;
        const r  = T(local, 0, 1.2, 20, 380, Easing.easeOutQuart);
        const op = (1 - prog) * 0.9;
        const sw = T(local, 0, 1.2, 6, 0.5, Easing.easeOutCubic);
        return <circle key={i} cx={360} cy={640} r={r} fill="none" stroke={COL.cy} strokeWidth={sw} opacity={op} />;
      })}
      {t < 2.7 && t > 2.15 && (() => {
        const local = t - 2.15;
        const r = T(local, 0, 0.55, 0, 280, Easing.easeOutQuart);
        const op = T(local, 0, 0.15, 0, 1, Easing.easeOutCubic)
                 * (1 - T(local, 0.2, 0.55, 0, 1, Easing.easeInCubic));
        return <circle cx={360} cy={640} r={r} fill="#fff" opacity={op * 0.9} />;
      })()}
    </svg>
  );
}

// ─── Crate (8 phases в одном компоненте) ────────────────
function Crate({ crateType }: { crateType: CrateType }) {
  const t = useTime();
  const cfg = CRATE_TYPES[crateType];
  if (t >= 4.5) return null;

  const yEntry = T(t, 0, 0.9, 200, 0, Easing.easeOutBack);
  const settleBob = (t > 1.0 && t < 1.8) ? Math.sin((t - 1.0) * 4) * 4 : 0;
  const tremor    = (t > 1.8 && t < 2.2) ? Math.sin(t * 60) * 4 * ((t - 1.8) / 0.4) : 0;

  const bodyOpacity = T(t, 2.4, 3.5, 1, 0, Easing.easeInQuad);
  const bodySink    = T(t, 2.4, 3.5, 0, 40, Easing.easeInQuad);

  const lidY  = T(t, 2.15, 4.0, 0, -800, Easing.easeOutCubic);
  const lidRot = T(t, 2.15, 4.0, 0, -45, Easing.easeOutCubic);
  const lidOp  = T(t, 2.15, 2.3, 1, 1, Easing.linear) - T(t, 3.0, 4.0, 0, 1, Easing.linear);
  const lidFlying = t > 2.15 && t < 4.0;
  const lidStill  = t < 2.15;

  const scanY  = T(t, 1.0, 1.8, -20, 60, Easing.easeInOutCubic);
  const scanOp = (t > 1.0 && t < 1.8) ? 0.9 : 0;
  const crackOp = T(t, 1.85, 2.2, 0, 1, Easing.easeOutCubic);

  return (
    <div style={{
      position: 'absolute', left: 360, top: 560,
      transform: `translate(-50%, ${yEntry + settleBob + bodySink}px) translateX(${tremor}px)`,
      filter: t > 2.2 ? `drop-shadow(0 0 40px ${cfg.accent})` : 'none'
    }}>
      <svg width="320" height="320" viewBox="0 0 100 100" style={{ overflow: 'visible' }}>
        <defs>
          <linearGradient id="crate-lid-g" x1="0" x2="1" y1="0" y2="1">
            <stop offset="0%"   stopColor={cfg.lid1}/>
            <stop offset="100%" stopColor={cfg.lid2}/>
          </linearGradient>
          <radialGradient id="crate-shine-g" cx="50%" cy="40%" r="60%">
            <stop offset="0%"  stopColor={cfg.accent} stopOpacity=".3"/>
            <stop offset="70%" stopColor={cfg.accent} stopOpacity="0"/>
          </radialGradient>
          <filter id="crate-glow"><feGaussianBlur stdDeviation="1.2"/></filter>
          <clipPath id="crate-lid-clip"><path d="M20 38 L50 22 L80 38 L50 54 Z"/></clipPath>
        </defs>

        <ellipse cx="50" cy="92" rx="34" ry="4" fill={cfg.accent} opacity={bodyOpacity * 0.18}/>

        {/* BODY */}
        <g opacity={bodyOpacity}>
          <path d="M20 38 L20 70 L50 86 L50 54 Z" fill={cfg.lid2} stroke={cfg.accent} strokeWidth="1.4"/>
          <path d="M80 38 L80 70 L50 86 L50 54 Z" fill="#08222A" stroke={cfg.accent} strokeWidth="1.4"/>
          <line x1="20" y1="48" x2="50" y2="64" stroke={cfg.accent} strokeWidth="0.6" opacity=".5"/>
          <line x1="20" y1="58" x2="50" y2="74" stroke={cfg.accent} strokeWidth="0.6" opacity=".5"/>
          <line x1="80" y1="48" x2="50" y2="64" stroke={cfg.accent} strokeWidth="0.6" opacity=".5"/>
          <line x1="80" y1="58" x2="50" y2="74" stroke={cfg.accent} strokeWidth="0.6" opacity=".5"/>
          <path d="M42 60 L50 64 L58 60 L50 56 Z" fill="#06181C" stroke={cfg.accent} strokeWidth="1.2"/>
          <circle cx="50" cy="60" r="1.4" fill={cfg.accent}>
            <animate attributeName="opacity" values="1;0.3;1" dur="0.8s" repeatCount="indefinite"/>
          </circle>
        </g>

        {/* LID flying */}
        {lidFlying && (
          <g opacity={lidOp} transform={`translate(0 ${lidY}) rotate(${lidRot} 50 38)`}>
            <path d="M20 38 L50 22 L80 38 L50 54 Z" fill="url(#crate-lid-g)" stroke={cfg.accent} strokeWidth="1.4"/>
            <path d="M20 38 L50 22 L80 38 L50 54 Z" fill="url(#crate-shine-g)"/>
            <g transform="translate(50 38)">
              <polygon points="0,-9 8,-4.5 8,4.5 0,9 -8,4.5 -8,-4.5" fill={cfg.lid2} stroke={cfg.accent} strokeWidth="1.2"/>
              <text x="0" y="3" textAnchor="middle" fontFamily="Chakra Petch" fontSize="8" fontWeight="700" fill={cfg.accent}>{cfg.label}</text>
            </g>
          </g>
        )}

        {/* LID resting (scanline + cracks) */}
        {lidStill && (
          <g>
            <path d="M20 38 L50 22 L80 38 L50 54 Z" fill="url(#crate-lid-g)" stroke={cfg.accent} strokeWidth="1.4"/>
            <path d="M20 38 L50 22 L80 38 L50 54 Z" fill="url(#crate-shine-g)"/>
            {scanOp > 0 && (
              <g clipPath="url(#crate-lid-clip)">
                <line x1="20" y1={38 + scanY} x2="80" y2={38 + scanY} stroke={cfg.accent} strokeWidth="0.8" opacity={scanOp}/>
                <rect x="18" y={38 + scanY - 6} width="64" height="6" fill={cfg.accent} opacity={scanOp * 0.15}/>
              </g>
            )}
            {crackOp > 0.01 && (
              <g opacity={crackOp} stroke={cfg.accent} strokeWidth="0.6" fill="none" filter="url(#crate-glow)">
                <path d="M30 30 L42 36 L38 42 L52 44 L58 38 L70 42"/>
                <path d="M50 22 L48 30 L54 34 L50 40"/>
                <path d="M42 36 L36 38 M58 38 L62 36"/>
              </g>
            )}
            <g transform="translate(50 38)">
              <polygon points="0,-9 8,-4.5 8,4.5 0,9 -8,4.5 -8,-4.5" fill={cfg.lid2} stroke={cfg.accent} strokeWidth="1.2"/>
              <text x="0" y="3" textAnchor="middle" fontFamily="Chakra Petch" fontSize="8" fontWeight="700" fill={cfg.accent}>{cfg.label}</text>
            </g>
          </g>
        )}
      </svg>

      {/* Auth readout */}
      {t > 0.9 && t < 2.1 && (
        <div style={{
          position: 'absolute', left: '50%', top: 340, transform: 'translateX(-50%)',
          fontFamily: 'JetBrains Mono, monospace', fontSize: 10,
          color: cfg.accent, letterSpacing: '.2em', textTransform: 'uppercase',
          textAlign: 'center', whiteSpace: 'nowrap',
          opacity: T(t, 0.9, 1.1, 0, 1) * T(t, 1.9, 2.1, 1, 0, Easing.linear)
        }}>
          {t < 1.7 ? (
            <span>{t < 1.3 ? '◆ AUTH · ███_____' : t < 1.6 ? '◆ AUTH · ██████__' : '◆ AUTH · ████████'}</span>
          ) : (
            <span style={{ color: COL.mg }}>⚠ ВЗЛОМ ПЕЧАТИ ⚠</span>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Card в полёте (использует GameCard для единого вида) ──
// Breakdown карт по корпам — отображается в верхней пустой зоне сцены
// (между TopBar и картами). Появляется одновременно с Summary chip.
function CorpBreakdown({ cards }: { cards: OpenedCard[] }) {
  const t = useTime();
  if (t < 6.5) return null;
  const op = T(t, 6.5, 7.2, 0, 1, Easing.easeOutCubic);

  const counts = useMemo(() => {
    const map = new Map<string, number>();
    for (const c of cards) {
      map.set(c.faction, (map.get(c.faction) || 0) + 1);
    }
    return Array.from(map.entries()).sort((a, b) => b[1] - a[1]);
  }, [cards]);

  const FACTION_FULL: Record<string, { name: string; color: string }> = {
    deus:      { name: 'DEUS SYSTEMS',      color: '#8FB2FF' },
    syntetics: { name: 'SYNTETICS SYNDICATE', color: '#E7FF3D' },
    global:    { name: 'GLOBAL SECURITY',   color: '#FFC83D' },
    livlife:   { name: 'LIV LIFE',          color: '#3DFFA5' },
    neutral:   { name: 'STREET',            color: '#9599A6' }
  };

  return (
    <div style={{
      position: 'absolute', top: 280, left: 0, right: 0,
      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10,
      opacity: op,
      pointerEvents: 'none'
    }}>
      <div style={{
        fontFamily: 'JetBrains Mono, monospace', fontSize: 12,
        letterSpacing: '.24em', color: COL.tLo, textTransform: 'uppercase'
      }}>
        ⟢  ПО КОРПАМ  ⟣
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'flex-start' }}>
        {counts.map(([faction, count]) => {
          const f = FACTION_FULL[faction] || FACTION_FULL.neutral;
          return (
            <div key={faction} style={{
              display: 'grid', gridTemplateColumns: '260px 56px',
              alignItems: 'center', gap: 16,
              padding: '4px 10px',
              background: 'rgba(10,10,16,.6)',
              borderLeft: `3px solid ${f.color}`
            }}>
              <span style={{
                fontFamily: 'Chakra Petch, sans-serif', fontSize: 18,
                fontWeight: 700, color: f.color, letterSpacing: '.04em'
              }}>{f.name}</span>
              <span style={{
                fontFamily: 'JetBrains Mono, monospace', fontSize: 22,
                fontWeight: 700, color: COL.white, textAlign: 'right'
              }}>{count}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Card({ data, full, spawnTime, slotX, slotY, onClick }: {
  data: OpenedCard;
  full: CardData | null;
  spawnTime: number;
  slotX: number;
  slotY: number;
  onClick?: () => void;
}) {
  const t = useTime();
  const local = t - spawnTime;
  if (local < 0) return null;

  const dur = data.rarity === 'epic' || data.rarity === 'legendary' ? 1.1
            : data.rarity === 'rare' ? 0.9 : 0.7;
  const prog = Math.min(local / dur, 1);
  const ease = Easing.easeOutCubic(prog);

  const srcX = 360, srcY = 640;
  const midY = Math.min(srcY, slotY) - 220;
  const x = srcX + (slotX - srcX) * ease;
  const y = (1 - ease) * (1 - ease) * srcY + 2 * (1 - ease) * ease * midY + ease * ease * slotY;

  const scale = T(local, 0, 0.15, 0.2, 1.0, Easing.easeOutBack);
  const spin = (1 - prog) * 540;

  const landed = local > dur;
  const landTime = local - dur;
  const hoverY = landed ? Math.sin((local - dur) * 2.5) * 3 : 0;
  const rarityFactor = data.rarity === 'epic' || data.rarity === 'legendary' ? 1.0
                     : data.rarity === 'rare' ? 0.7 : 0.4;
  const landPulse = landed ? Math.max(0, 1 - landTime * 1.5) * rarityFactor : 0;
  const rGlow = RARITY_GLOW[data.rarity];

  // Если в данных не нашли карту (не должно быть, но fallback) — рендерим
  // минимальный CardData с тем что прислал сервер.
  const cardData: CardData = full || {
    id: data.id,
    name_ru: data.name_ru,
    faction: data.faction as any,
    type: 'unit',
    rarity: data.rarity,
    atk: 0, armor: 0, hp: 0,
    keywords: [],
    exclusive_hero: null
  };

  return (
    <div style={{
      position: 'absolute', left: x, top: y,
      width: 140, height: 200,
      transform: `translate(-50%, calc(-50% + ${hoverY}px)) scale(${scale}) rotateY(${spin}deg)`,
      transformStyle: 'preserve-3d',
      // Тап работает только когда карта приземлилась (после флайта).
      pointerEvents: landed ? 'auto' : 'none',
      cursor: landed && onClick ? 'pointer' : 'default',
      zIndex: 10,
      filter: landed ? `drop-shadow(0 0 ${20 + landPulse * 30}px ${rGlow})` : 'none'
    }} onClick={(e) => {
      if (!landed) return;
      e.stopPropagation(); // не триггерить skip
      onClick?.();
    }}>
      <GameCard card={cardData} variant="compact" glowIntensity={landPulse} />
    </div>
  );
}

// ─── Card layout ────────────────────────────────────
function computeSlots(count: number): { x: number; y: number }[] {
  // Адаптируем под реальное количество карт. 1-4 — один ряд, 5-8 — два ряда.
  // Карта 140×200. Расстояние center-to-center с зазором:
  //   spread=160 → 20px gap по горизонтали (раньше 0 = сливались)
  //   row top/bot разнесены на 240 → 40px gap по вертикали
  const cx = 360;
  const spread = 160;
  if (count <= 4) {
    const y = 960;
    return Array.from({ length: count }, (_, i) => ({
      x: cx + (i - (count - 1) / 2) * spread,
      y
    }));
  }
  // Карты подняты повыше: 800/1020 (раньше 840/1080) — чтобы Summary
  // chip не наезжал на нижний ряд. Bottom of bottom-row = 1020+100=1120,
  // Summary at bottom:60 ≈ y=1220, зазор ~100px.
  const top = 800, bot = 1020;
  const row1 = Math.min(4, Math.ceil(count / 2));
  const row2 = count - row1;
  const slots: { x: number; y: number }[] = [];
  for (let i = 0; i < row1; i++) slots.push({ x: cx + (i - (row1 - 1) / 2) * spread, y: top });
  for (let i = 0; i < row2; i++) slots.push({ x: cx + (i - (row2 - 1) / 2) * spread, y: bot });
  return slots;
}

// ─── Summary chip + кнопка Забрать ──────────────────
function Summary({ cards, onClaim }: { cards: OpenedCard[]; onClaim: () => void }) {
  const t = useTime();
  if (t < 6.5) return null;
  const op = T(t, 6.5, 7.2, 0, 1, Easing.easeOutCubic);
  const counts = cards.reduce((a, c) => { a[c.rarity] = (a[c.rarity] || 0) + 1; return a; }, {} as Record<string, number>);

  return (
    <div style={{
      position: 'absolute', bottom: 60, left: 0, right: 0,
      display: 'flex', justifyContent: 'center',
      opacity: op
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 16,
        padding: '14px 24px',
        background: 'rgba(10,10,16,.85)',
        backdropFilter: 'blur(12px)',
        border: '1px solid rgba(61,240,255,.4)',
        boxShadow: '0 0 32px rgba(61,240,255,.25)',
        clipPath: 'polygon(12px 0, 100% 0, 100% calc(100% - 12px), calc(100% - 12px) 100%, 0 100%, 0 12px)'
      }}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          <div style={{ fontFamily: 'JetBrains Mono', fontSize: 9, letterSpacing: '.2em', color: COL.tLo, textTransform: 'uppercase' }}>Получено</div>
          <div style={{ fontFamily: 'Chakra Petch', fontSize: 24, fontWeight: 700, color: COL.white, letterSpacing: '.02em' }}>{cards.length} карт</div>
        </div>
        <div style={{ width: 1, height: 36, background: 'rgba(160,170,200,.2)' }} />
        <div style={{ display: 'flex', gap: 10 }}>
          {(['common','rare','epic','legendary'] as RarityApi[]).map(r => counts[r] ? (
            <RarityChip key={r} label={`${counts[r]}×`} color={RARITY_COLOR[r]} />
          ) : null)}
        </div>
        <div style={{ width: 1, height: 36, background: 'rgba(160,170,200,.2)' }} />
        <button
          onClick={onClaim}
          style={{
            padding: '10px 18px',
            background: COL.cy, color: '#06131A',
            fontFamily: 'Chakra Petch', fontWeight: 600, fontSize: 14, letterSpacing: '.08em', textTransform: 'uppercase',
            clipPath: 'polygon(8px 0, 100% 0, 100% calc(100% - 8px), calc(100% - 8px) 100%, 0 100%, 0 8px)',
            boxShadow: '0 0 16px rgba(61,240,255,.45)',
            border: 'none', cursor: 'pointer'
          }}
        >
          Забрать
        </button>
      </div>
    </div>
  );
}

// ─── Звуковая дорожка сцены (Этап 71) ────────────────────
// Играет звук при пересечении временной метки сценой (метки = фазы из README
// §5 + spawn-времена карт). Сильно опоздавшие метки (> 0.4с, напр. late-mount)
// не доигрываем, чтобы не выстреливала очередь догоняющих звуков.
function SoundCues({ cards, spawns }: { cards: OpenedCard[]; spawns: number[] }) {
  const t = useTime();
  const fired = useRef<Set<string>>(new Set());
  const cues = useMemo(() => {
    const list: { at: number; sfx: string }[] = [
      { at: 0.05, sfx: 'crateHum' },   // появление контейнера
      { at: 1.0,  sfx: 'crateScan' },  // скан-авторизация
      { at: 1.8,  sfx: 'crateCrack' }, // трещины + тряска
      { at: 2.15, sfx: 'crateBurst' }, // взрыв (файл)
      { at: 6.5,  sfx: 'crateDone' },  // итоговая плашка
    ];
    spawns.forEach((at, i) => {
      const r = cards[i]?.rarity;
      list.push({ at, sfx: r === 'epic' || r === 'legendary' ? 'crateRare' : 'crateTick' });
    });
    return list;
  }, [cards, spawns]);
  useEffect(() => {
    for (const cue of cues) {
      const key = `${cue.at}:${cue.sfx}`;
      if (t >= cue.at && !fired.current.has(key)) {
        fired.current.add(key);
        if (t - cue.at < 0.4) play(cue.sfx);
      }
    }
  }, [t, cues]);
  return null;
}

function RarityChip({ label, color }: { label: string; color: string }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 6,
      padding: '5px 10px',
      border: `1px solid ${color}`,
      borderRadius: 3,
      background: `${color}10`
    }}>
      <div style={{ width: 6, height: 6, background: color, borderRadius: '50%', boxShadow: `0 0 6px ${color}` }} />
      <span style={{ fontFamily: 'Chakra Petch', fontWeight: 700, fontSize: 14, color, letterSpacing: '.04em' }}>{label}</span>
    </div>
  );
}

// ─── Главная сцена ─────────────────────────────────
interface Props {
  cards: OpenedCard[];
  crateType: CrateType;
  onClaim: () => void;
  /** Skip-callback — вызывается при тапе после 3.0s. */
  onSkip?: () => void;
}

export function CrateOpeningScene({ cards, crateType, onClaim, onSkip }: Props) {
  // Этап 71: страховочный прогрев файла взрыва (обычно уже в кэше с unlockSfx;
  // до burst-фазы 2.15с — успевает догрузиться даже с холодного старта).
  useEffect(() => { preloadSfx(['crateBurst']); }, []);

  // Сортируем по rarity-возрастанию для драматичного pacing (handoff §11).
  const sorted = useMemo(() => {
    const order: Record<RarityApi, number> = { common: 0, rare: 1, epic: 2, legendary: 3 };
    return [...cards].sort((a, b) => order[a.rarity] - order[b.rarity]);
  }, [cards]);

  // Подгружаем полные данные карт из @data (статы, keywords, ability_text).
  // Сервер в open-pack отдаёт только {id, rarity, name_ru, faction} — реальные
  // характеристики берутся из локального пула.
  const fullCards = useMemo(() => sorted.map(c => getCardData(c.id)), [sorted]);

  const slots = useMemo(() => computeSlots(sorted.length), [sorted.length]);

  // Spawn-расписание: чем редчее карта, тем больше пауза перед.
  const cardSpawns = useMemo(() => {
    const baseSpawn = 2.45;
    return sorted.map((c, i) => {
      const prev = i === 0 ? null : sorted[i - 1];
      if (!prev) return baseSpawn;
      const gap = c.rarity === 'legendary' ? 0.5
                : c.rarity === 'epic' ? 0.34
                : c.rarity === 'rare' && prev.rarity !== 'rare' ? 0.5
                : c.rarity === 'rare' ? 0.34
                : 0.16;
      // На каждой итерации добавляем gap к предыдущему spawn.
      // (Реализуется через reduce ниже.)
      return gap;
    }).reduce<number[]>((acc, gap, i) => {
      const prev = i === 0 ? baseSpawn : acc[i - 1];
      acc.push(i === 0 ? baseSpawn : prev + gap);
      return acc;
    }, []);
  }, [sorted]);

  // Skip after 3s.
  const [skipped, setSkipped] = useState(false);
  const onScreenTap = () => {
    // Skip разрешён только после 3s suspense build.
    // Реальная проверка времени делается в SkipHandler (доступ к useTime).
    setSkipped(true);
  };

  // Когда юзер ткнул после 3s — фастфорвард: сразу summary + cards в финальных слотах.
  // В простейшей реализации: вызываем onSkip → закрытие/переход к Claim.
  if (skipped && onSkip) {
    onSkip();
    return null;
  }

  return (
    <Stage
      width={720}
      height={1280}
      duration={8.5}
      background={COL.bg}
      style={{ zIndex: 500, cursor: 'pointer' }}
    >
      <SceneInner crateType={crateType} sorted={sorted} fullCards={fullCards} slots={slots} cardSpawns={cardSpawns} onClaim={onClaim} onTap={onScreenTap} />
    </Stage>
  );
}

// SceneInner вынесен чтобы получить доступ к useTime() для skip-логики
// (Stage предоставляет TimelineContext дочерним компонентам).
function SceneInner({ crateType, sorted, fullCards, slots, cardSpawns, onClaim, onTap }: {
  crateType: CrateType;
  sorted: OpenedCard[];
  fullCards: (CardData | null)[];
  slots: { x: number; y: number }[];
  cardSpawns: number[];
  onClaim: () => void;
  onTap: () => void;
}) {
  const t = useTime();
  const skipAllowed = t >= 3.0;
  // Модалка с полной версией карты (открывается при тапе на карту).
  const [previewIdx, setPreviewIdx] = useState<number | null>(null);
  return (
    <div
      style={{ position: 'absolute', inset: 0 }}
      onClick={() => { if (skipAllowed && previewIdx === null) onTap(); }}
    >
      <SoundCues cards={sorted} spawns={cardSpawns} />
      <Background />
      <TopBar crateType={crateType} />
      <CorpBreakdown cards={sorted} />
      <Crate crateType={crateType} />
      <Shockwave />
      <ParticleBurst />
      {sorted.map((c, i) => (
        <Card
          key={c.id + ':' + i}
          data={c}
          full={fullCards[i]}
          spawnTime={cardSpawns[i]}
          slotX={slots[i].x}
          slotY={slots[i].y}
          onClick={() => setPreviewIdx(i)}
        />
      ))}
      <Summary cards={sorted} onClaim={onClaim} />
      {/* Skip-подсказка после 3s (скрывается при открытой модалке) */}
      {t > 3.0 && t < 6.4 && previewIdx === null && (
        <div style={{
          position: 'absolute', bottom: 30, left: 0, right: 0,
          textAlign: 'center', pointerEvents: 'none',
          fontFamily: 'JetBrains Mono, monospace', fontSize: 10,
          color: COL.tLo, letterSpacing: '.2em', textTransform: 'uppercase'
        }}>
          ⟢ тап для пропуска · тап на карту → подробно ⟣
        </div>
      )}
      {/* Модалка с полной картой — поверх сцены */}
      {previewIdx !== null && (() => {
        const c = sorted[previewIdx];
        const cardData: CardData = fullCards[previewIdx] || {
          id: c.id, name_ru: c.name_ru, faction: c.faction as any,
          type: 'unit', rarity: c.rarity,
          atk: 0, armor: 0, hp: 0, keywords: [], exclusive_hero: null
        };
        return (
          <div
            style={{
              position: 'absolute', inset: 0,
              background: 'rgba(0,0,0,0.85)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              padding: 40,
              zIndex: 100
            }}
            onClick={(e) => { e.stopPropagation(); setPreviewIdx(null); }}
          >
            <div
              style={{ width: 420, maxWidth: '90%' }}
              onClick={(e) => e.stopPropagation()}
            >
              <GameCard card={cardData} variant="full" />
              <button
                onClick={() => setPreviewIdx(null)}
                style={{
                  marginTop: 16,
                  width: '100%',
                  padding: 14,
                  background: 'var(--bg-2, #101018)',
                  color: COL.tMd,
                  border: '0.5px solid var(--line, rgba(160,170,200,0.18))',
                  fontFamily: 'Chakra Petch, sans-serif',
                  fontSize: 12, fontWeight: 700,
                  letterSpacing: '0.1em',
                  cursor: 'pointer'
                }}
              >ЗАКРЫТЬ</button>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
